/**
 * <DataTable/> — the ONE reusable table shell used across the platform.
 *
 * A thin, unopinionated wrapper that gives every table the same:
 *   - search box (client-side or server-side via `onSearch`)
 *   - filter chips (declarative via `filters` prop)
 *   - sorting (click column header)
 *   - pagination (client-side, unless `serverPagination` is set)
 *   - sticky header
 *   - loading + empty states
 *   - optional export button
 *   - optional bulk-actions bar when rows are selected
 *
 * NOT trying to be TanStack Table — this is a light glue layer that lets us
 * migrate every existing table without inheriting a heavy dep. If any page
 * outgrows this, it can drop back to a bespoke table for that one use case.
 *
 * Usage:
 *
 *   <DataTable
 *     testId="orgs-table"
 *     columns={[
 *       { key: "name",  label: "Name",    sortable: true, render: (row) => <b>{row.name}</b> },
 *       { key: "plan",  label: "Plan",    sortable: true, width: "120px" },
 *       { key: "seats", label: "Seats",   sortable: true, align: "right", render: (r) => r.seats ?? "—" },
 *       { key: "_act",  label: "",        width: "80px",  render: (r) => <button>Open</button> },
 *     ]}
 *     data={rows}
 *     loading={loading}
 *     searchPlaceholder="Search organizations…"
 *     searchKeys={["name", "id", "plan"]}
 *     filters={[
 *       { key: "plan",   label: "Plan",   options: [{value:"individual",label:"Individual"},{value:"business",label:"Business"}] },
 *       { key: "status", label: "Status", options: [{value:"active",label:"Active"},{value:"pending",label:"Pending"}] },
 *     ]}
 *     bulkActions={[{label: "Suspend", onRun: (rows) => ...}]}
 *     emptyState={<EmptyState .../>}
 *     rowKey={(r) => r.id}
 *   />
 */
import React, { useMemo, useState } from "react";
import { MagnifyingGlass, CaretUp, CaretDown, DownloadSimple, X } from "@phosphor-icons/react";

const DEFAULT_PAGE_SIZES = [10, 25, 50, 100];

export default function DataTable({
  testId = "data-table",
  columns,                     // [{key, label, sortable?, render?, width?, align?}]
  data,                        // array of rows
  loading = false,
  rowKey = (r) => r.id ?? r._id ?? JSON.stringify(r),
  rowTestId,                   // optional (row) => string — override auto-generated row testid
  searchPlaceholder = "Search…",
  searchKeys,                  // client-side substring keys, or omit for no search
  onSearch,                    // server-side search: (q) => void
  filters = [],                // [{key, label, options: [{value, label}]}]
  initialFilters,              // {key: value}
  bulkActions,                 // [{label, onRun, destructive?, testId?}]
  onExport,                    // () => void to fire CSV export
  exportLabel = "Export CSV",
  emptyState,                  // React node — falls back to a generic pill
  pageSize: initialPageSize = 25,
  serverPagination = false,    // when true, DataTable doesn't slice `data`
  totalRows,                   // required when serverPagination=true
  page: controlledPage,
  onPageChange,
  className = "",
}) {
  const [q, setQ] = useState("");
  const [sort, setSort] = useState({ key: null, dir: "asc" });
  const [activeFilters, setActiveFilters] = useState(() => initialFilters || {});
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [internalPage, setInternalPage] = useState(0);
  const [selected, setSelected] = useState(new Set());

  const page = controlledPage !== undefined ? controlledPage : internalPage;

  // Filter → search → sort → paginate. All client-side unless serverPagination.
  const filteredSorted = useMemo(() => {
    let out = Array.isArray(data) ? data : [];

    // Column filters
    for (const [k, v] of Object.entries(activeFilters)) {
      if (v) out = out.filter(row => row[k] === v);
    }

    // Client-side search
    if (!onSearch && q && searchKeys && searchKeys.length > 0) {
      const needle = q.toLowerCase();
      out = out.filter(row =>
        searchKeys.some(k => String(row[k] ?? "").toLowerCase().includes(needle))
      );
    }

    // Sort
    if (sort.key) {
      out = [...out].sort((a, b) => {
        const av = a[sort.key];
        const bv = b[sort.key];
        if (av === bv) return 0;
        if (av === undefined || av === null) return 1;
        if (bv === undefined || bv === null) return -1;
        const cmp = typeof av === "number" && typeof bv === "number"
          ? av - bv
          : String(av).localeCompare(String(bv));
        return sort.dir === "asc" ? cmp : -cmp;
      });
    }

    return out;
  }, [data, q, sort, activeFilters, searchKeys, onSearch]);

  // Fire server-side search on debounce
  React.useEffect(() => {
    if (!onSearch) return;
    const h = setTimeout(() => onSearch(q), 250);
    return () => clearTimeout(h);
  }, [q, onSearch]);

  const total = serverPagination ? (totalRows ?? filteredSorted.length) : filteredSorted.length;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const currentPage = Math.min(page, pageCount - 1);
  const paged = serverPagination
    ? filteredSorted
    : filteredSorted.slice(currentPage * pageSize, currentPage * pageSize + pageSize);

  const goToPage = (n) => {
    const next = Math.max(0, Math.min(pageCount - 1, n));
    if (onPageChange) onPageChange(next); else setInternalPage(next);
  };

  const toggleSort = (colKey) => {
    setSort(s => s.key === colKey
      ? { key: colKey, dir: s.dir === "asc" ? "desc" : "asc" }
      : { key: colKey, dir: "asc" });
  };

  const setFilter = (k, v) => setActiveFilters(prev => {
    const next = { ...prev };
    if (v === "" || v === undefined) delete next[k]; else next[k] = v;
    return next;
  });

  const clearAll = () => {
    setActiveFilters({});
    setQ("");
    setSort({ key: null, dir: "asc" });
    goToPage(0);
  };

  const activeFilterCount = Object.keys(activeFilters).filter(k => activeFilters[k]).length;

  // Selection
  const allSelected = paged.length > 0 && paged.every(r => selected.has(rowKey(r)));
  const someSelected = paged.some(r => selected.has(rowKey(r)));
  const toggleAll = () => {
    setSelected(prev => {
      const next = new Set(prev);
      const keys = paged.map(rowKey);
      if (allSelected) keys.forEach(k => next.delete(k));
      else keys.forEach(k => next.add(k));
      return next;
    });
  };
  const toggleRow = (row) => {
    const k = rowKey(row);
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k); else next.add(k);
      return next;
    });
  };
  const selectedRows = data ? data.filter(r => selected.has(rowKey(r))) : [];
  const clearSelection = () => setSelected(new Set());

  const hasControls = (searchKeys || onSearch) || filters.length > 0 || onExport;

  return (
    <div className={`bg-white border border-[#ECEFF5] rounded-[20px] overflow-hidden shadow-[0_1px_2px_rgba(11,21,38,.03),0_8px_24px_-6px_rgba(11,21,38,.06),0_24px_60px_-30px_rgba(37,99,235,.14)] ${className}`} data-testid={testId}>
      {hasControls && (
        <div className="border-b border-[#F6F8FB] px-4 py-3 flex flex-wrap items-center gap-2" data-testid={`${testId}-controls`}>
          {(searchKeys || onSearch) && (
            <div className="relative flex-1 min-w-[200px] max-w-md">
              <MagnifyingGlass size={13} className="absolute left-2.5 top-2.5 text-slate-400"/>
              <input
                value={q}
                onChange={(e) => { setQ(e.target.value); goToPage(0); }}
                placeholder={searchPlaceholder}
                className="w-full pl-8 pr-3 py-2 border border-[#ECEFF5] rounded-[10px] text-[12.5px] font-medium focus:outline-none focus:border-[#2563EB] focus:shadow-[0_0_0_3px_rgba(37,99,235,.12)] transition-shadow"
                data-testid={`${testId}-search`}
                aria-label={searchPlaceholder}
              />
              {q && (
                <button
                  onClick={() => setQ("")}
                  className="absolute right-2 top-1.5 text-slate-400 hover:text-slate-700"
                  aria-label="Clear search"
                  data-testid={`${testId}-search-clear`}
                ><X size={12}/></button>
              )}
            </div>
          )}

          {filters.map(f => (
            <select
              key={f.key}
              value={activeFilters[f.key] || ""}
              onChange={(e) => { setFilter(f.key, e.target.value); goToPage(0); }}
              className="px-2.5 py-2 border border-[#ECEFF5] rounded-[10px] text-[12.5px] font-medium bg-white"
              data-testid={`${testId}-filter-${f.key}`}
              aria-label={f.label}
            >
              <option value="">{f.label}: All</option>
              {f.options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          ))}

          {(activeFilterCount > 0 || q || sort.key) && (
            <button
              onClick={clearAll}
              className="text-[11px] font-mono uppercase tracking-widest text-slate-500 hover:text-slate-800 px-2"
              data-testid={`${testId}-clear`}
            >Clear</button>
          )}

          <div className="flex-1"/>

          {onExport && (
            <button
              onClick={onExport}
              className="flex items-center gap-1 text-[11px] font-mono uppercase tracking-widest border border-[#ECEFF5] hover:border-[#2563EB] rounded-[10px] px-2.5 py-2 transition-colors"
              data-testid={`${testId}-export`}
            ><DownloadSimple size={12}/> {exportLabel}</button>
          )}
        </div>
      )}

      {/* Bulk actions bar — appears above the header when >=1 row selected */}
      {bulkActions && bulkActions.length > 0 && selected.size > 0 && (
        <div className="border-b border-slate-100 px-3 py-2 flex items-center gap-3 bg-sky-50" data-testid={`${testId}-bulk-bar`}>
          <span className="text-[12px] font-semibold text-sky-900">{selected.size} selected</span>
          <button onClick={clearSelection} className="text-[11px] text-sky-700 hover:underline" data-testid={`${testId}-bulk-clear`}>Clear</button>
          <div className="flex-1"/>
          {bulkActions.map(a => (
            <button
              key={a.label}
              onClick={() => a.onRun(selectedRows)}
              className={`text-[11px] font-mono uppercase tracking-widest px-2 py-1 rounded ${a.destructive ? "bg-red-100 text-red-800 hover:bg-red-200" : "bg-white border border-sky-300 text-sky-900 hover:bg-sky-100"}`}
              data-testid={a.testId || `${testId}-bulk-${a.label.toLowerCase().replace(/\s+/g, "-")}`}
            >{a.label}</button>
          ))}
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-[13px] border-collapse" data-testid={`${testId}-table`}>
          <thead className="sticky top-0 z-[1] bg-[#FAFBFD] border-b border-[#ECEFF5]">
            <tr>
              {bulkActions && bulkActions.length > 0 && (
                <th className="px-2 py-2 w-8">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    ref={el => { if (el) el.indeterminate = someSelected && !allSelected; }}
                    onChange={toggleAll}
                    aria-label="Select all rows"
                    data-testid={`${testId}-select-all`}
                  />
                </th>
              )}
              {columns.map(col => (
                <th
                  key={col.key}
                  className={`px-4 py-3 text-[10px] font-mono uppercase tracking-widest text-slate-400 text-${col.align || "left"} ${col.sortable ? "cursor-pointer select-none hover:text-slate-800" : ""}`}
                  style={col.width ? { width: col.width } : undefined}
                  onClick={col.sortable ? () => toggleSort(col.key) : undefined}
                  data-testid={`${testId}-th-${col.key}`}
                  scope="col"
                >
                  <span className="inline-flex items-center gap-1">
                    {col.label}
                    {col.sortable && sort.key === col.key && (
                      sort.dir === "asc" ? <CaretUp size={10}/> : <CaretDown size={10}/>
                    )}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={columns.length + (bulkActions ? 1 : 0)} className="px-4 py-10 text-center text-[12px] text-slate-500" data-testid={`${testId}-loading`}>Loading…</td></tr>
            ) : paged.length === 0 ? (
              <tr><td colSpan={columns.length + (bulkActions ? 1 : 0)} className="px-4 py-10" data-testid={`${testId}-empty`}>
                {emptyState || (
                  <div className="text-center text-[12px] text-slate-500">No matches</div>
                )}
              </td></tr>
            ) : paged.map(row => {
              const key = rowKey(row);
              const isSel = selected.has(key);
              const rowTid = rowTestId ? rowTestId(row) : `${testId}-row-${key}`;
              return (
                <tr key={key} className={`border-b border-[#F6F8FB] hover:bg-[#F8FAFF] hover:shadow-[inset_3px_0_0_#2563EB] transition-all ${isSel ? "bg-sky-50" : ""}`} data-testid={rowTid}>
                  {bulkActions && bulkActions.length > 0 && (
                    <td className="px-2 py-2">
                      <input
                        type="checkbox"
                        checked={isSel}
                        onChange={() => toggleRow(row)}
                        aria-label={`Select row ${key}`}
                        data-testid={`${testId}-select-${key}`}
                      />
                    </td>
                  )}
                  {columns.map(col => (
                    <td key={col.key} className={`px-4 py-3.5 text-${col.align || "left"}`}>
                      {col.render ? col.render(row) : (row[col.key] ?? "—")}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {(total > pageSize || serverPagination) && (
        <div className="border-t border-slate-100 px-3 py-2 flex items-center gap-3 text-[11px] text-slate-600" data-testid={`${testId}-pager`}>
          <div>
            {total === 0 ? "0 rows" : (
              <>Showing {currentPage * pageSize + 1}–{Math.min((currentPage + 1) * pageSize, total)} of {total}</>
            )}
          </div>
          <div className="flex-1"/>
          <label className="flex items-center gap-1">
            <span>Rows:</span>
            <select
              value={pageSize}
              onChange={(e) => { setPageSize(Number(e.target.value)); goToPage(0); }}
              className="border border-slate-300 rounded px-1 py-0.5 text-[11px] bg-white"
              data-testid={`${testId}-page-size`}
            >
              {DEFAULT_PAGE_SIZES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
          <button onClick={() => goToPage(currentPage - 1)} disabled={currentPage === 0}
                  className="px-2 py-1 border border-slate-300 rounded disabled:opacity-30 hover:border-slate-500"
                  data-testid={`${testId}-prev`}>Prev</button>
          <span data-testid={`${testId}-page-info`}>Page {currentPage + 1} / {pageCount}</span>
          <button onClick={() => goToPage(currentPage + 1)} disabled={currentPage >= pageCount - 1}
                  className="px-2 py-1 border border-slate-300 rounded disabled:opacity-30 hover:border-slate-500"
                  data-testid={`${testId}-next`}>Next</button>
        </div>
      )}
    </div>
  );
}
